import {
  CHASSIS_CODE_LIST_IDS,
  CHASSIS_CODE_LISTS,
  type ChassisCodeEntry,
  type ChassisCodeListId,
  normalizeChassisCode,
} from "@bt/parts-core";
import { MUNICH_CHASSIS_CODE_STYLES } from "./make-packages/munich";
import { ZUFFENHAUSEN_CHASSIS_CODE_STYLES } from "./make-packages/zuffenhausen";

// The built-in lists and what a code means (normalizing, aliases, fit) are shared with the
// collector (@bt/parts-core). What a garage does with them, and their colours, stay here.

/** Picked codes the workspace doesn't have yet, each once, compared normalized. */
export function chassisCodesToAdd(
  existingNames: readonly string[],
  picked: readonly string[],
): string[] {
  const seen = new Set(existingNames.map(normalizeChassisCode));
  const added: string[] = [];
  for (const code of picked) {
    const key = normalizeChassisCode(code);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    added.push(code.trim());
  }
  return added;
}

/** The colours a chassis code can have; each is a Tailwind hue (the web app maps them). */
export const CHASSIS_CODE_COLORS = [
  "sky",
  "blue",
  "indigo",
  "cyan",
  "teal",
  "emerald",
  "green",
  "lime",
  "violet",
  "purple",
  "amber",
  "yellow",
  "orange",
  "red",
  "rose",
  "pink",
  "fuchsia",
  "slate",
  "stone",
] as const;

export type ChassisCodeColor = (typeof CHASSIS_CODE_COLORS)[number];

/**
 * How a built-in list's codes are coloured: the series a generation belongs to, and the hues each
 * series' generations take turns with. The lists are shared data; their colours are the app's.
 */
export interface ChassisCodeListStyle {
  series: (entry: ChassisCodeEntry) => string;
  hues: Readonly<Record<string, readonly ChassisCodeColor[]>>;
}

export const CHASSIS_CODE_STYLES: Record<ChassisCodeListId, ChassisCodeListStyle> = {
  ...MUNICH_CHASSIS_CODE_STYLES,
  ...ZUFFENHAUSEN_CHASSIS_CODE_STYLES,
};

/**
 * Built-in codes take their colour from their list's series, its generations taking turns with
 * the series' hues in list order. Body sub-codes take their generation's colour.
 */
const BUILT_IN_COLORS: ReadonlyMap<string, ChassisCodeColor> = (() => {
  const colors = new Map<string, ChassisCodeColor>();
  const bodies: [string, string][] = [];
  for (const list of CHASSIS_CODE_LIST_IDS) {
    const spec = CHASSIS_CODE_STYLES[list];
    const turns = new Map<string, number>();
    for (const entry of CHASSIS_CODE_LISTS[list]) {
      const key = normalizeChassisCode(entry.code);
      if (entry.parent) {
        bodies.push([key, normalizeChassisCode(entry.parent)]);
        continue;
      }
      const series = spec.series(entry);
      const hues = spec.hues[series] ?? [];
      const turn = turns.get(series) ?? 0;
      turns.set(series, turn + 1);
      colors.set(key, hues[turn % hues.length] ?? "slate");
    }
  }
  for (const [key, parent] of bodies) colors.set(key, colors.get(parent) ?? "slate");
  return colors;
})();

/** Colours for codes not in the built-in lists: never grey, so every code stands out. */
const OWN_CODE_COLORS = CHASSIS_CODE_COLORS.filter((c) => c !== "slate" && c !== "stone");

/**
 * The colour a chassis code has unless the user picked another: from the built-in lists, or for
 * the user's own codes one derived from the code itself, so it is the same everywhere.
 */
export function defaultChassisCodeColor(key: string): ChassisCodeColor {
  const normalized = normalizeChassisCode(key);
  const builtIn = BUILT_IN_COLORS.get(normalized);
  if (builtIn) return builtIn;
  let hash = 0;
  for (const char of normalized) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  return OWN_CODE_COLORS[hash % OWN_CODE_COLORS.length] ?? "blue";
}

/** Each built-in code's list, by normalized key; the first list that has a code keeps it. */
const BUILT_IN_LISTS: ReadonlyMap<string, ChassisCodeListId> = (() => {
  const lists = new Map<string, ChassisCodeListId>();
  for (const list of CHASSIS_CODE_LIST_IDS) {
    for (const entry of CHASSIS_CODE_LISTS[list]) {
      const key = normalizeChassisCode(entry.code);
      if (!lists.has(key)) lists.set(key, list);
    }
  }
  return lists;
})();

/** The built-in list a chassis code comes from ("motorrad" for "K50"), or null for a garage's own. */
export function builtInChassisCodeList(key: string): ChassisCodeListId | null {
  return BUILT_IN_LISTS.get(normalizeChassisCode(key)) ?? null;
}
